import * as React from 'react';
import { FC, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'reactstrap';
import { SettingsItemContentProps } from '../SettingsItem';
import { CashRegisterSettings as CashRegisterSettingsData, EntityType } from '../../../models';
import { AutocompleteTextField, CheckInputField, ComboField, MultipleCheckboxes } from '../../../components/Forms/fields';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { useGeneralSettings } from '../../../store/settings/hooks';
import { getEntitiesDataItems } from '../../../store/entities/selectors';
import { UserEntity } from '../../../store/entities/state';
import { PermissionEnum } from '../../../models';
import Typography from '../../../components/Typography';
import { loadEntities } from '../../../store/entities/actions';
import { getUsersAsync } from '../../../ajax/requests';

export interface CashRegisterSettingsProps extends SettingsItemContentProps {
    values: CashRegisterSettingsData;
    onChange: (settings: CashRegisterSettingsData, save?: boolean) => void;
}

const CashRegisterSettings: FC<CashRegisterSettingsProps> = ({
    id,
    values,
    onChange,
}) => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const generalSettings = useGeneralSettings();
    const users = useSelector(getEntitiesDataItems<UserEntity>('users'));

    React.useEffect(() => {
        dispatch(loadEntities('users', () => getUsersAsync(), 'NewResult'))
    }, [])

    const paymentMethods = useMemo(() => generalSettings?.paymentMethods ?? [], [generalSettings?.paymentMethods]);

    const nonAdminUsers = useMemo(() =>
        users.filter(u => !u.permissions?.includes(PermissionEnum.Administrator)),
        [users]
    );

    const userOptions = useMemo(() =>
        nonAdminUsers.map(u => ({
            label: u.name || u.username,
            value: u.username,
        })),
        [nonAdminUsers]
    );

    const handleEnabledChange = (enabled: boolean) => {
        onChange({ ...values, enabled });
    };

    const handleAllPaymentTypesChange = (allPaymentTypes: boolean) => {
        onChange({
            ...values,
            allPaymentTypes,
            paymentTypes: allPaymentTypes ? [] : values.paymentTypes,
        });
    };

    const handlePaymentTypesChange = (paymentTypes: string[]) => {
        onChange({ ...values, paymentTypes });
    };

    const handleCashPaymentTypeChange = (cashPaymentType: string | undefined) => {
        onChange({ ...values, cashPaymentType: cashPaymentType || undefined });
    };

    const handleSharedRegisterNamesChange = (sharedRegisterNames: string[]) => {
        onChange({ ...values, sharedRegisterNames });
    };

    const handleApproversChange = (selectedLabels: string[]) => {
        const selectedIds = userOptions
            .filter(opt => selectedLabels.includes(opt.label))
            .map(opt => opt.value);
        onChange({ ...values, nonAdminApprovers: selectedIds });
    };

    const selectedApproverLabels = useMemo(() =>
        userOptions
            .filter(opt => values.nonAdminApprovers?.includes(opt.value))
            .map(opt => opt.label),
        [userOptions, values.nonAdminApprovers]
    );

    return (
        <Form style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-M)' }}>
            <CheckInputField
                id={`${id}-enabled`}
                label={t(TK.cashRegisterEnabled)}
                helpText={t(TK.cashRegisterEnabledHelp)}
                value={values.enabled}
                onChange={handleEnabledChange}
            />

            {values.enabled && (
                <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)' }}>
                        <Typography block style={{ margin: 0 }}>{t(TK.cashRegisterPaymentTypes)}</Typography>
                        <CheckInputField
                            id={`${id}-all-payment-types`}
                            label={t(TK.cashRegisterAllPaymentTypes)}
                            value={values.allPaymentTypes}
                            onChange={handleAllPaymentTypesChange}
                        />
                        {!values.allPaymentTypes && paymentMethods.length > 0 && (
                            <MultipleCheckboxes
                                style={{ marginLeft: 'var(--spacer-S)' }}
                                values={values.paymentTypes ?? []}
                                options={paymentMethods}
                                onChange={handlePaymentTypesChange}
                            />
                        )}
                    </div>

                    {paymentMethods.length > 0 && (
                        <ComboField
                            label={t(TK.cashRegisterCashPaymentType)}
                            helpText={t(TK.cashRegisterCashPaymentTypeHelp)}
                            value={values.cashPaymentType ?? ''}
                            options={[
                                { value: '', label: t(TK.cashRegisterCashPaymentTypeAuto) },
                                ...paymentMethods.map(m => ({ value: m, label: m })),
                            ]}
                            onChange={handleCashPaymentTypeChange}
                        />
                    )}

                    {userOptions.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)' }}>
                            <Typography block style={{ margin: 0 }}>{t(TK.cashRegisterApprovers)}</Typography>
                            <Typography size="smaller" block style={{ margin: 0, color: 'var(--text-muted)' }}>
                                {t(TK.cashRegisterApproversHelp)}
                            </Typography>
                            <MultipleCheckboxes
                                style={{ marginLeft: 'var(--spacer-S)' }}
                                values={selectedApproverLabels}
                                options={userOptions.map(opt => opt.label)}
                                onChange={handleApproversChange}
                            />
                        </div>
                    )}

                    <AutocompleteTextField
                        label={t(TK.cashRegisterSharedNames)}
                        helpText={t(TK.cashRegisterSharedNamesHelp)}
                        multiSelect
                        options={values.sharedRegisterNames ?? []}
                        values={values.sharedRegisterNames ?? []}
                        onValuesChanged={handleSharedRegisterNamesChange}
                        onAddNewOption={name => handleSharedRegisterNamesChange([...(values.sharedRegisterNames ?? []), name])}
                    />
                </>
            )}
        </Form>
    );
};

export default CashRegisterSettings;
